import { printVoteReceipt } from '../../utils/printUtils';
import PrintButton from '../shared/PrintButton';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Crown, Star, Trophy, Users, Lock, Unlock, RefreshCw,
  ChevronDown, ChevronUp, CheckCircle, AlertCircle,
  Loader2, BarChart2, Gift, ShieldCheck
} from 'lucide-react';

// ── DROP 39B-1: ContestantVotingBooth — Option B (live from eventRegistrations) ──

interface Contestant {
  id: string;
  name: string;
  community: string;
  communityIcon: string;
  bio: string;
  photoUrl: string | null;
  votes: number;
  contestantNo: string;
}

interface VoteBundle {
  id: string;
  label: string;
  votes: number;
  price: number;
  badge: string;
  popular?: boolean;
}

interface VotingConfig {
  isOpen: boolean;
  votingType: string;
  bundles: VoteBundle[];
}

interface AdminContestant extends Contestant {
  email: string;
  voteHistory: { ref: string; bundleLabel: string; count: number; amount: number; at: string }[];
  approvedAt: string;
}

interface Props {
  currentUser?: {
    email?: string;
    systemRole?: string;
    role?: string;
    displayName?: string;
  } | null;
}

const S = {
  page:    { background: '#070D0A', minHeight: '100vh', padding: '24px 16px', fontFamily: 'Plus Jakarta Sans, sans-serif' },
  card:    { background: '#0E1812', border: '1px solid #1C2E22', borderRadius: 12, padding: 20 },
  heading: { fontFamily: 'Cinzel, serif', color: '#C9A84C' },
  gold:    '#C9A84C',
  green:   '#0F5132',
  red:     '#ef4444',
  muted:   'rgba(237,232,220,0.5)',
  text:    '#EDE8DC',
  surface: '#121E16',
  border:  '#1C2E22',
};

function getToken(): string {
  return localStorage.getItem('erue_auth_token') || sessionStorage.getItem('erue_auth_token') || '';
}
function authHeaders(): Record<string, string> {
  const t = getToken();
  return t ? { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + t }
           : { 'Content-Type': 'application/json' };
}

const MEDAL: Record<number, string> = { 0: '👑', 1: '🥈', 2: '🥉' };

export default function ContestantVotingBooth({ currentUser }: Props) {
  const userRole = (currentUser?.systemRole || currentUser?.role || '').toUpperCase();
  const isAdmin  = ['PLATFORM_ADMIN','FESTIVAL_COORDINATOR','COMMUNITY_MANAGER'].includes(userRole);

  const [tab, setTab]                 = useState<'public' | 'admin'>('public');
  const [contestants, setContestants] = useState<Contestant[]>([]);
  const [config, setConfig]           = useState<VotingConfig | null>(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState<string | null>(null);

  const [selectedContestant, setSelectedContestant] = useState<Contestant | null>(null);
  const [selectedBundle, setSelectedBundle]          = useState<VoteBundle | null>(null);
  const [voterName, setVoterName]                    = useState('');
  const [voterEmail, setVoterEmail]                  = useState('');
  const [voteLoading, setVoteLoading]                = useState(false);
  const [voteMsg, setVoteMsg]                        = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [adminContestants, setAdminContestants] = useState<AdminContestant[]>([]);
  const [adminLoading, setAdminLoading]          = useState(false);
  const [expanded, setExpanded]                  = useState<string | null>(null);
  const [toggleLoading, setToggleLoading]        = useState(false);
  const [adminMsg, setAdminMsg]                  = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref    = params.get('ref') || params.get('reference') || params.get('trxref');
    if (ref && ref.startsWith('vote_')) {
      verifyVotePayment(ref);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const loadPublic = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [cRes, vRes] = await Promise.all([
        fetch('/api/public/contestants'),
        fetch('/api/public/voting-config'),
      ]);
      const cData = await cRes.json();
      const vData = await vRes.json();
      if (cData.success) setContestants(cData.contestants || []);
      if (vData.success) setConfig(vData.config);
    } catch { setError('Failed to load contestants. Please try again.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadPublic(); }, [loadPublic]);

  const loadAdmin = useCallback(async () => {
    setAdminLoading(true);
    try {
      const res  = await fetch('/api/admin/contestants', { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setAdminContestants(data.contestants || []);
    } catch { setAdminMsg('Failed to load admin data.'); }
    finally { setAdminLoading(false); }
  }, []);

  useEffect(() => {
    if (tab === 'admin' && isAdmin) loadAdmin();
  }, [tab, isAdmin, loadAdmin]);

  async function verifyVotePayment(ref: string) {
    try {
      const res  = await fetch('/api/voting/verify', {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ reference: ref })
      });
      const data = await res.json();
      if (data.success) {
        setVoteMsg({ type: 'success', text: data.alreadyVerified
          ? 'Votes already credited!'
          : data.votesAdded + ' votes credited to ' + data.contestantName + '! Total: ' + data.totalVotes });
        loadPublic();
      } else {
        setVoteMsg({ type: 'error', text: data.error || 'Vote verification failed.' });
      }
    } catch { setVoteMsg({ type: 'error', text: 'Network error during vote verification.' }); }
  }

  async function handleVotePurchase() {
    if (!selectedContestant || !selectedBundle || !voterEmail) return;
    setVoteLoading(true); setVoteMsg(null);
    try {
      const res  = await fetch('/api/voting/purchase', {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ contestantId: selectedContestant.id,
          bundleId: selectedBundle.id, voterName, voterEmail }),
      });
      const data = await res.json();
      if (!data.success) { setVoteMsg({ type: 'error', text: data.error || 'Purchase failed.' }); return; }
      if (data.previewMode) {
        setVoteMsg({ type: 'success', text: 'Preview mode — votes would be credited in live mode.' });
        setSelectedContestant(null); return;
      }
      if (data.authorizationUrl) window.location.href = data.authorizationUrl;
    } catch { setVoteMsg({ type: 'error', text: 'Network error. Please try again.' }); }
    finally { setVoteLoading(false); }
  }

  async function handleToggleVoting() {
    if (!config) return;
    setToggleLoading(true); setAdminMsg(null);
    try {
      const res  = await fetch('/api/admin/voting-config', {
        method: 'PUT', headers: authHeaders(),
        body: JSON.stringify({ isOpen: !config.isOpen }),
      });
      const data = await res.json();
      if (data.success) {
        setConfig(data.config);
        setAdminMsg('Voting ' + (data.config.isOpen ? 'OPENED' : 'CLOSED') + ' successfully.');
        loadPublic();
      } else { setAdminMsg('Error: ' + (data.error || 'Failed.')); }
    } catch { setAdminMsg('Network error.'); }
    finally { setToggleLoading(false); }
  }

  const sorted      = [...contestants].sort((a, b) => b.votes - a.votes);
  const adminSorted = [...adminContestants].sort((a, b) => b.votes - a.votes);
  const totalVotes   = adminSorted.reduce((s, c) => s + c.votes, 0);
  const totalRevenue = adminSorted.reduce(
    (s, c) => s + c.voteHistory.reduce((vs, v) => vs + (v.amount || 0), 0), 0
  );

  function TabBtn({ id, label }: { id: 'public' | 'admin'; label: string }) {
    const active = tab === id;
    return (
      <button onClick={() => setTab(id)} style={{
        padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer',
        background: active ? S.gold : 'transparent',
        color: active ? '#070D0A' : S.muted,
        fontWeight: active ? 700 : 400,
        fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 14,
      }}>{label}</button>
    );
  }

  function VoteModal() {
    if (!selectedContestant || !config) return null;
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 }}>
        <div style={{ ...S.card, maxWidth: 480, width: '100%', position: 'relative' }}>
          <button onClick={() => { setSelectedContestant(null); setSelectedBundle(null); setVoteMsg(null); }}
            style={{ position: 'absolute', top: 12, right: 12, background: 'none', border: 'none',
              color: S.muted, cursor: 'pointer', fontSize: 20 }}>✕</button>
          <h3 style={{ ...S.heading, fontSize: 18, marginBottom: 4 }}>Vote for {selectedContestant.name}</h3>
          <p style={{ color: S.muted, fontSize: 13, marginBottom: 16 }}>
            {selectedContestant.community} {selectedContestant.communityIcon}
          </p>
          <p style={{ color: S.text, fontSize: 13, marginBottom: 10, fontWeight: 600 }}>Select a Vote Bundle:</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
            {(config.bundles || []).map((bundle) => (
              <button key={bundle.id} onClick={() => setSelectedBundle(bundle)} style={{
                padding: '12px 16px', borderRadius: 8, cursor: 'pointer', textAlign: 'left',
                background: selectedBundle?.id === bundle.id ? 'rgba(201,168,76,0.15)' : S.surface,
                border: selectedBundle?.id === bundle.id ? '1.5px solid ' + S.gold : '1px solid ' + S.border,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: S.text, fontWeight: 600 }}>
                    {bundle.badge} {bundle.label}
                    {bundle.popular && <span style={{ marginLeft: 8, fontSize: 10, background: S.gold,
                      color: '#070D0A', padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>POPULAR</span>}
                  </span>
                  <span style={{ color: S.gold, fontWeight: 700 }}>₦{bundle.price.toLocaleString()}</span>
                </div>
                <div style={{ color: S.muted, fontSize: 12, marginTop: 3 }}>{bundle.votes} votes</div>
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
            <input placeholder="Your Name (optional)" value={voterName}
              onChange={e => setVoterName(e.target.value)}
              style={{ padding: '10px 14px', borderRadius: 8, border: '1px solid ' + S.border,
                background: S.surface, color: S.text, fontSize: 14, outline: 'none' }} />
            <input placeholder="Your Email *" value={voterEmail} type="email"
              onChange={e => setVoterEmail(e.target.value)}
              style={{ padding: '10px 14px', borderRadius: 8, border: '1px solid ' + S.border,
                background: S.surface, color: S.text, fontSize: 14, outline: 'none' }} />
          </div>
          {voteMsg && (
            <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 12,
              background: voteMsg.type === 'success' ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)',
              border: '1px solid ' + (voteMsg.type === 'success' ? '#22c55e' : S.red),
              color: voteMsg.type === 'success' ? '#22c55e' : S.red, fontSize: 13 }}>{voteMsg.text}</div>
          )}
          <button onClick={handleVotePurchase}
            disabled={!selectedBundle || !voterEmail || voteLoading}
            style={{ width: '100%', padding: '12px', borderRadius: 8, border: 'none',
              background: (!selectedBundle || !voterEmail || voteLoading) ? '#1C2E22' : S.gold,
              color: (!selectedBundle || !voterEmail || voteLoading) ? S.muted : '#070D0A',
              fontWeight: 700, fontSize: 15, cursor: (!selectedBundle || !voterEmail) ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            {voteLoading ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Gift size={16} />}
            {voteLoading ? 'Processing...' : selectedBundle
              ? 'Pay ₦' + selectedBundle.price.toLocaleString() + ' — ' + selectedBundle.votes + ' Votes'
              : 'Select a Bundle'}
          </button>
        </div>
      </div>
    );
  }

  function PublicView() {
    if (loading) return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
        <Loader2 size={32} style={{ color: S.gold, animation: 'spin 1s linear infinite' }} />
      </div>
    );
    if (error) return (
      <div style={{ ...S.card, textAlign: 'center', padding: 40 }}>
        <AlertCircle size={32} style={{ color: S.red, marginBottom: 12 }} />
        <p style={{ color: S.red }}>{error}</p>
        <button onClick={loadPublic} style={{ marginTop: 16, padding: '8px 20px', borderRadius: 8,
          border: 'none', background: S.gold, color: '#070D0A', fontWeight: 700, cursor: 'pointer' }}>
          Retry
        </button>
      </div>
    );
    return (
      <>
        {config && (
          <div style={{ ...S.card, marginBottom: 20,
            background: config.isOpen ? 'rgba(15,81,50,0.3)' : 'rgba(239,68,68,0.08)',
            border: '1px solid ' + (config.isOpen ? '#0F5132' : 'rgba(239,68,68,0.3)'),
            display: 'flex', alignItems: 'center', gap: 12 }}>
            {config.isOpen ? <Unlock size={20} style={{ color: '#22c55e' }} />
                           : <Lock size={20} style={{ color: S.red }} />}
            <div>
              <p style={{ color: config.isOpen ? '#22c55e' : S.red, fontWeight: 700, margin: 0 }}>
                {config.isOpen ? 'Voting is OPEN' : 'Voting is Currently CLOSED'}
              </p>
              <p style={{ color: S.muted, fontSize: 12, margin: 0 }}>
                {config.isOpen ? 'Cast your votes for your favourite contestant below'
                               : 'Voting will open soon — check back later'}
              </p>
            </div>
          </div>
        )}
        {voteMsg && (
          <div style={{ ...S.card, marginBottom: 20,
            background: voteMsg.type === 'success' ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)',
            border: '1px solid ' + (voteMsg.type === 'success' ? '#22c55e' : S.red),
            display: 'flex', alignItems: 'center', gap: 12 }}>
            {voteMsg.type === 'success'
              ? <CheckCircle size={20} style={{ color: '#22c55e' }} />
              : <AlertCircle size={20} style={{ color: S.red }} />}
            <p style={{ color: voteMsg.type === 'success' ? '#22c55e' : S.red, margin: 0, fontSize: 14 }}>
              {voteMsg.text}
            </p>
          </div>
        )}
        {voteMsg?.type === 'success' && selectedBundle && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8, marginBottom: 8 }}>
            <PrintButton
              label="Print Vote Receipt"
              size="sm"
              variant="green"
              onClick={() => printVoteReceipt({
                contestantName: contestants.find(c => c.id === selectedContestant)?.name || 'Contestant',
                contestantVillage: contestants.find(c => c.id === selectedContestant)?.village || '',
                voterEmail: voterEmail,
                voterName: (voterName as string) || '',
                bundleLabel: selectedBundle?.label || '',
                votes: selectedBundle?.votes || 0,
                amountPaid: selectedBundle?.price || 0,
                timestamp: new Date().toLocaleString('en-NG'),
              })}
            />
          </div>
        )}
        {sorted.length === 0 && (
          <div style={{ ...S.card, textAlign: 'center', padding: 60 }}>
            <Crown size={48} style={{ color: S.gold, marginBottom: 16, opacity: 0.5 }} />
            <h3 style={{ ...S.heading, fontSize: 20, marginBottom: 8 }}>No Contestants Yet</h3>
            <p style={{ color: S.muted, fontSize: 14 }}>
              Miss ERUE Culture 2026 contestants will appear here once registrations are approved.
            </p>
          </div>
        )}
        {sorted.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
            {sorted.map((c, idx) => (
              <div key={c.id} style={{ ...S.card, position: 'relative',
                border: idx === 0 ? '1.5px solid ' + S.gold : '1px solid ' + S.border }}>
                {idx < 3 && (
                  <div style={{ position: 'absolute', top: 12, right: 12, fontSize: 22 }}>{MEDAL[idx]}</div>
                )}
                <div style={{ width: 72, height: 72, borderRadius: '50%', marginBottom: 12,
                  background: 'rgba(201,168,76,0.1)', border: '2px solid ' + S.border,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 28, overflow: 'hidden' }}>
                  {c.photoUrl
                    ? <img src={c.photoUrl} alt={c.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : '👸'}
                </div>
                {c.contestantNo && (
                  <div style={{ fontSize: 10, color: S.gold, fontWeight: 700, letterSpacing: 1,
                    marginBottom: 4, textTransform: 'uppercase' }}>CONTESTANT #{c.contestantNo}</div>
                )}
                <h3 style={{ ...S.heading, fontSize: 16, marginBottom: 2 }}>{c.name}</h3>
                <p style={{ color: S.muted, fontSize: 12, marginBottom: 8 }}>{c.communityIcon} {c.community}</p>
                {c.bio && (
                  <p style={{ color: S.text, fontSize: 12, marginBottom: 12, lineHeight: 1.5 }}>
                    {c.bio.slice(0, 100)}{c.bio.length > 100 ? '…' : ''}
                  </p>
                )}
                <div style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ color: S.muted, fontSize: 11 }}>Votes</span>
                    <span style={{ color: S.gold, fontWeight: 700, fontSize: 14 }}>{c.votes.toLocaleString()}</span>
                  </div>
                  {sorted[0].votes > 0 && (
                    <div style={{ height: 4, background: S.border, borderRadius: 2, overflow: 'hidden' }}>
                      <div style={{ height: '100%', borderRadius: 2, background: S.gold,
                        width: Math.round((c.votes / sorted[0].votes) * 100) + '%', transition: 'width 0.5s ease' }} />
                    </div>
                  )}
                </div>
                <button onClick={() => {
                    if (!config?.isOpen) return;
                    setSelectedContestant(c); setSelectedBundle(null);
                    setVoterName(''); setVoterEmail(''); setVoteMsg(null);
                  }}
                  disabled={!config?.isOpen}
                  style={{ width: '100%', padding: '9px', borderRadius: 8, border: 'none',
                    background: config?.isOpen ? S.gold : '#1C2E22',
                    color: config?.isOpen ? '#070D0A' : S.muted,
                    fontWeight: 700, fontSize: 13,
                    cursor: config?.isOpen ? 'pointer' : 'not-allowed',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  <Star size={13} />
                  {config?.isOpen ? 'Vote Now' : 'Voting Closed'}
                </button>
              </div>
            ))}
          </div>
        )}
      </>
    );
  }

  function AdminView() {
    if (!isAdmin) return (
      <div style={{ ...S.card, textAlign: 'center', padding: 40 }}>
        <ShieldCheck size={32} style={{ color: S.muted, marginBottom: 12 }} />
        <p style={{ color: S.muted }}>Admin access required.</p>
      </div>
    );
    return (
      <>
        <div style={{ ...S.card, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <h3 style={{ ...S.heading, fontSize: 16, margin: 0 }}>Voting Administration</h3>
              <p style={{ color: S.muted, fontSize: 12, margin: 0 }}>
                Contestants auto-populate from approved Miss ERUE Culture 2026 registrations
              </p>
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <button onClick={loadAdmin} disabled={adminLoading}
                style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid ' + S.border,
                  background: 'transparent', color: S.muted, cursor: 'pointer',
                  display: 'flex', gap: 6, alignItems: 'center' }}>
                <RefreshCw size={14} style={{ animation: adminLoading ? 'spin 1s linear infinite' : 'none' }} />
                Refresh
              </button>
              {config && (
                <button onClick={handleToggleVoting} disabled={toggleLoading}
                  style={{ padding: '7px 16px', borderRadius: 8, border: 'none',
                    background: config.isOpen ? 'rgba(239,68,68,0.15)' : 'rgba(34,197,94,0.15)',
                    color: config.isOpen ? S.red : '#22c55e',
                    fontWeight: 700, cursor: 'pointer', display: 'flex', gap: 6, alignItems: 'center' }}>
                  {toggleLoading ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                    : config.isOpen ? <Lock size={14} /> : <Unlock size={14} />}
                  {config.isOpen ? 'Close Voting' : 'Open Voting'}
                </button>
              )}
            </div>
          </div>
          {adminMsg && (
            <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 6,
              background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.3)',
              color: S.gold, fontSize: 13 }}>{adminMsg}</div>
          )}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 16 }}>
          {[
            { label: 'Contestants', value: adminSorted.length, icon: <Users size={16} /> },
            { label: 'Total Votes', value: totalVotes.toLocaleString(), icon: <Star size={16} /> },
            { label: 'Vote Revenue', value: '\u20a6' + totalRevenue.toLocaleString(), icon: <BarChart2 size={16} /> },
            { label: 'Status', value: config?.isOpen ? 'OPEN' : 'CLOSED',
              icon: config?.isOpen ? <Unlock size={16} /> : <Lock size={16} />,
              accent: config?.isOpen ? '#22c55e' : S.red },
          ].map((stat, i) => (
            <div key={i} style={{ ...S.card, textAlign: 'center' }}>
              <div style={{ color: (stat as {accent?: string}).accent || S.gold, marginBottom: 6 }}>{stat.icon}</div>
              <div style={{ color: (stat as {accent?: string}).accent || S.gold, fontSize: 20, fontWeight: 700 }}>{stat.value}</div>
              <div style={{ color: S.muted, fontSize: 11 }}>{stat.label}</div>
            </div>
          ))}
        </div>
        {adminSorted.length === 0 && !adminLoading && (
          <div style={{ ...S.card, textAlign: 'center', padding: 40 }}>
            <Crown size={36} style={{ color: S.gold, opacity: 0.4, marginBottom: 12 }} />
            <h4 style={{ ...S.heading, fontSize: 15, marginBottom: 8 }}>No Approved Contestants</h4>
            <p style={{ color: S.muted, fontSize: 13 }}>
              Go to <strong style={{ color: S.gold }}>Registrations &rarr; Event Registrations</strong> and
              approve Miss ERUE Culture 2026 applicants. Approved contestants appear here automatically.
            </p>
          </div>
        )}
        {adminSorted.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <h4 style={{ ...S.heading, fontSize: 14, margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Trophy size={14} /> Leaderboard — {adminSorted.length} Contestants
            </h4>
            {adminSorted.map((c, idx) => {
              const isExp = expanded === c.id;
              return (
                <div key={c.id} style={{ ...S.card, border: idx === 0 ? '1px solid ' + S.gold : '1px solid ' + S.border }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}
                    onClick={() => setExpanded(isExp ? null : c.id)}>
                    <div style={{ fontSize: 20, minWidth: 28, textAlign: 'center' }}>
                      {MEDAL[idx] || (idx + 1)}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: S.text, fontWeight: 700, fontSize: 14 }}>{c.name}</div>
                      <div style={{ color: S.muted, fontSize: 11 }}>{c.community} · {c.email}</div>
                    </div>
                    <div style={{ textAlign: 'right', marginRight: 8 }}>
                      <div style={{ color: S.gold, fontWeight: 700, fontSize: 16 }}>{c.votes.toLocaleString()}</div>
                      <div style={{ color: S.muted, fontSize: 10 }}>votes</div>
                    </div>
                    {isExp ? <ChevronUp size={16} style={{ color: S.muted }} />
                           : <ChevronDown size={16} style={{ color: S.muted }} />}
                  </div>
                  {isExp && (
                    <div style={{ marginTop: 14, borderTop: '1px solid ' + S.border, paddingTop: 14 }}>
                      {c.voteHistory.length === 0
                        ? <p style={{ color: S.muted, fontSize: 12 }}>No vote transactions yet.</p>
                        : (
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                            <thead>
                              <tr>{['Bundle','Votes','Amount','Date'].map(h => (
                                <th key={h} style={{ color: S.muted, textAlign: 'left', padding: '4px 8px',
                                  borderBottom: '1px solid ' + S.border }}>{h}</th>
                              ))}</tr>
                            </thead>
                            <tbody>
                              {c.voteHistory.map((v, vi) => (
                                <tr key={vi}>
                                  <td style={{ color: S.text, padding: '4px 8px' }}>{v.bundleLabel}</td>
                                  <td style={{ color: S.gold, padding: '4px 8px' }}>{v.count}</td>
                                  <td style={{ color: S.text, padding: '4px 8px' }}>&#8358;{(v.amount||0).toLocaleString()}</td>
                                  <td style={{ color: S.muted, padding: '4px 8px' }}>{new Date(v.at).toLocaleDateString()}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </>
    );
  }

  return (
    <div style={S.page}>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ ...S.heading, fontSize: 28, margin: 0 }}>
          Miss ERUE Culture 2026 — Voting Booth
        </h1>
        <p style={{ color: S.muted, fontSize: 14, marginTop: 6 }}>
          ERUE Festival of Uzairue Kingdom · December 24–30, 2026
        </p>
      </div>
      {isAdmin && (
        <div style={{ display: 'flex', gap: 4, marginBottom: 20,
          background: S.surface, borderRadius: 10, padding: 4, width: 'fit-content' }}>
          <TabBtn id="public" label="Public View" />
          <TabBtn id="admin" label="Admin Panel" />
        </div>
      )}
      {tab === 'public' ? <PublicView /> : <AdminView />}
      <VoteModal />
    </div>
  );
}
